import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import {
  MemoryGraph,
  TYPE,
  placePopover,
  type GraphCounts,
  type MemLink,
  type MemNode,
  type MemType,
  type MemoryGraphApi,
} from './memory-graph.ts';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Wires a {@link MemoryGraph} to a canvas + popover element: handles DPR
 * sizing, the requestAnimationFrame draw loop, pointer hover/select, and the
 * detail popover. Returns a stable imperative `api` (used by the chat
 * simulation to grow the graph) and reactive `counts` for the HUD.
 *
 * Degrades cleanly where there is no 2D context (e.g. jsdom under test): the
 * graph data and counts still work, only the animation is skipped.
 */
export function useMemoryGraph(
  canvasRef: RefObject<HTMLCanvasElement>,
  popRef: RefObject<HTMLDivElement>,
): { api: MemoryGraphApi; counts: GraphCounts } {
  const graphRef = useRef<MemoryGraph | null>(null);
  if (graphRef.current === null) {
    graphRef.current = new MemoryGraph();
  }
  const graph = graphRef.current;

  const [counts, setCounts] = useState<GraphCounts>({
    nodes: 0,
    edges: 0,
    active: 0,
  });
  const refreshCounts = useCallback(() => {
    setCounts(graph.counts());
  }, [graph]);

  const api = useMemo<MemoryGraphApi>(
    () => ({
      addMemory: (
        label: string,
        type: MemType,
        links?: MemLink[],
        detail?: string,
        recordId?: string,
      ) => {
        const id = graph.addMemory(label, type, links, detail, recordId);
        refreshCounts();
        return id;
      },
      setNodeText: (id: number, label: string, detail: string) => {
        // Label/detail only — node/edge/active counts are unchanged, so no
        // count refresh is needed.
        graph.setNodeText(id, label, detail);
      },
      removeRecords: (recordIds: readonly string[]) => {
        graph.removeRecords(recordIds);
        refreshCounts();
      },
      retainRecords: (keep: ReadonlySet<string>) => {
        graph.retainRecords(keep);
        refreshCounts();
      },
      clearMemoryRecords: () => {
        graph.clearMemoryRecords();
        refreshCounts();
      },
      setContext: (ids: number[]) => {
        graph.setContext(ids);
        refreshCounts();
      },
      activate: (ids: number[]) => {
        graph.activate(ids);
        refreshCounts();
      },
      clearContext: () => {
        graph.clearContext();
        refreshCounts();
      },
      reset: () => {
        graph.reset();
        refreshCounts();
      },
    }),
    [graph, refreshCounts],
  );

  // DEV-only test seam: expose the live graph so the isolated Memory Graph
  // harness's Playwright tests can read node positions for deterministic node
  // clicks. `import.meta.env.DEV` is statically false in prod → tree-shaken out.
  useEffect(() => {
    if (import.meta.env.DEV) {
      (window as unknown as { __memGraph?: MemoryGraph }).__memGraph = graph;
    }
  }, [graph]);

  // DPR sizing — runs before paint so the first frame and any seeding use real
  // bounds. The graph is positioned in CSS pixels (the context is pre-scaled).
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const wrap = canvas?.parentElement;
    if (!canvas || !wrap) {
      return;
    }
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const ctx = canvas.getContext('2d');
    const resize = (): void => {
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      graph.setBounds(w, h);
    };
    resize();
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    observer?.observe(wrap);
    window.addEventListener('resize', resize);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [graph, canvasRef]);

  // Animation + interaction. Skipped entirely without a 2D context.
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) {
      return;
    }
    let hover: MemNode | null = null;
    let selected: MemNode | null = null;
    let frame = 0;

    const pointerPos = (event: MouseEvent | TouchEvent): [number, number] => {
      const rect = canvas.getBoundingClientRect();
      const point =
        'touches' in event && (event.touches[0] as Touch | undefined) ? event.touches[0] : (event as MouseEvent);
      return [point.clientX - rect.left, point.clientY - rect.top];
    };
    const onMove = (event: MouseEvent): void => {
      const [mx, my] = pointerPos(event);
      hover = graph.nodeAt(mx, my);
      canvas.style.cursor = hover ? 'pointer' : 'default';
    };
    const onLeave = (): void => {
      hover = null;
    };
    const onPick = (event: MouseEvent | TouchEvent): void => {
      const [mx, my] = pointerPos(event);
      const hit = graph.nodeAt(mx, my);
      selected = hit && hit === selected ? null : hit;
      if (event.type === 'touchstart' && hit) {
        event.preventDefault();
      }
    };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('click', onPick);
    canvas.addEventListener('touchstart', onPick, { passive: false });

    const positionPopover = (node: MemNode): void => {
      const pop = popRef.current;
      const wrap = canvas.parentElement;
      if (!pop || !wrap) {
        return;
      }
      const meta = TYPE[node.type];
      pop.innerHTML = `
        <div class="om-mempop__head">
          <i style="background:${meta.color}"></i>
          <span class="om-mempop__type">${escapeHtml(meta.label)}</span>
          <span class="om-mempop__tier">${node.tier}</span>
        </div>
        <div class="om-mempop__label">${escapeHtml(node.label)}</div>
        ${node.detail ? `<div class="om-mempop__detail">${escapeHtml(node.detail)}</div>` : ''}
        <div class="om-mempop__foot">
          <span>${node.active ? '<b class="on">● in context</b>' : '○ stored (dormant)'}</span>
          <span>${escapeHtml(graph.linkSummary(node))}</span>
        </div>`;
      pop.style.display = 'block';
      const pw = pop.offsetWidth || 220;
      const ph = pop.offsetHeight || 150;
      // The popover is position:fixed, so place it in viewport coordinates: it
      // opens toward the roomier side of the page (out of the narrow inspector
      // panel, into the chat area) and is clamped fully on-screen. The canvas
      // fills the wrap, so its rect is the graph's coordinate origin.
      const rect = canvas.getBoundingClientRect();
      const { left, top } = placePopover(
        rect.left + node.x,
        rect.top + node.y,
        window.innerWidth,
        window.innerHeight,
        pw,
        ph,
      );
      pop.style.left = `${left}px`;
      pop.style.top = `${top}px`;
    };
    const hidePopover = (): void => {
      if (popRef.current) {
        popRef.current.style.display = 'none';
      }
    };

    const loop = (now: number): void => {
      graph.step();
      const focus = selected ?? hover;
      graph.draw(ctx, now, focus);
      if (focus) {
        positionPopover(focus);
      } else {
        hidePopover();
      }
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(frame);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('click', onPick);
      canvas.removeEventListener('touchstart', onPick);
    };
  }, [graph, canvasRef, popRef]);

  return { api, counts };
}
